import React, { useEffect, useState } from 'react';
import './ZonePreviewOverlay.scss';

interface Edge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

// Screen-space handles: the point a capture started from, each vertex a
// polygon capture has committed, and the live cursor vertex that follows the
// player. Drawn differently so the origin stays identifiable once a shape has
// several corners.
interface Handle {
  x: number;
  y: number;
  kind: 'origin' | 'vertex' | 'cursor';
}

interface UpdateDetail {
  visible: boolean;
  edges?: Edge[];
  handles?: Handle[];
  // Formatted by wildlifeZonePreviewService: the shapes it can draw
  // (rectangle, circle, polygon) have nothing in common to report, and a
  // circle's radius changes on every step, so the text is produced with the
  // geometry rather than reconstructed here.
  readout?: string;
  hint?: string;
  screenWidth?: number;
  screenHeight?: number;
}

const HANDLE_RADIUS: Record<Handle['kind'], number> = {
  origin: 9,
  vertex: 6,
  cursor: 5,
};

// Purely visual, always mounted, invisible until wildlifeZonePreviewService.ts
// dispatches an update (driven by the /markzone, /markradius and /markpoly
// chat commands - see wildlifeSystem.ts). Same "always active, manages its
// own visibility" convention as EmoteWheel/CitySelection/TargetMenu.
const ZonePreviewOverlay: React.FC = () => {
  const [state, setState] = useState<UpdateDetail>({ visible: false });

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as UpdateDetail;
      setState(detail ?? { visible: false });
    };
    window.addEventListener('skymp5-client:zonePreviewUpdate', handler);
    return () => window.removeEventListener('skymp5-client:zonePreviewUpdate', handler);
  }, []);

  if (!state.visible || !state.screenWidth || !state.screenHeight) return null;

  const edges = state.edges ?? [];
  const handles = state.handles ?? [];

  return (
    <div className="zone-preview-overlay">
      <svg
        className="zone-preview-overlay__svg"
        viewBox={`0 0 ${state.screenWidth} ${state.screenHeight}`}
        preserveAspectRatio="none"
      >
        {edges.map((edge, i) => (
          <line key={`e${i}`} x1={edge.x1} y1={edge.y1} x2={edge.x2} y2={edge.y2} />
        ))}
        {handles.map((handle, i) => (
          <circle
            key={`h${i}`}
            className={`zone-preview-overlay__handle zone-preview-overlay__handle--${handle.kind}`}
            cx={handle.x}
            cy={handle.y}
            r={HANDLE_RADIUS[handle.kind]}
          />
        ))}
      </svg>
      {edges.length === 0 && handles.length === 0 && (
        <div className="zone-preview-overlay__offscreen-hint">Zone hors champ de vision</div>
      )}
      {state.readout && <div className="zone-preview-overlay__readout">{state.readout}</div>}
      {state.hint && <div className="zone-preview-overlay__hint">{state.hint}</div>}
    </div>
  );
};

export default ZonePreviewOverlay;
